import { AccredibleRadioGroupOption } from '@accredible-frontend-v2/forms/radio-input';
import { AccredibleBaseFormComponent } from '@accredible-frontend-v2/forms/utils';
import {
  CustomAttribute,
  CustomAttributeOption,
  CustomAttributeType,
  CustomAttributeValue,
} from '@accredible-frontend-v2/models';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'custom-attribute-input',
  templateUrl: './custom-attribute-input.component.html',
  styleUrls: ['./custom-attribute-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomAttributeInputComponent
  extends AccredibleBaseFormComponent<{ field: string | boolean | string[] | boolean[] }>
  implements OnInit, OnChanges
{
  @Input()
  customAttribute: CustomAttribute;
  @Input()
  autoSubmit: boolean;

  @Output()
  onSubmit = new EventEmitter<CustomAttributeValue>();

  readonly customAttributeType = CustomAttributeType;

  radioInputOptions: AccredibleRadioGroupOption[];
  initialFormValue: CustomAttributeValue;
  canSave = false;
  isSaving = false;

  constructor(
    protected readonly _el: ElementRef<HTMLElement>,
    private readonly _fb: UntypedFormBuilder,
  ) {
    super(_el);
  }

  @Input()
  set savingCustomAttributeIds(ids: number[]) {
    this.isSaving = ids.includes(this.customAttribute.id);
  }

  ngOnInit(): void {
    if (this.customAttribute.type === CustomAttributeType.SELECT_ONE) {
      this.radioInputOptions = mapOptionsToRadioInputOptions(this.customAttribute.options);
    }
    this._initForm();

    this._setupFormValueTracking();
  }

  ngOnChanges(simpleChanges: SimpleChanges): void {
    if (
      simpleChanges.customAttribute?.currentValue !== simpleChanges.customAttribute?.previousValue
    ) {
      this.initialFormValue = simpleChanges.customAttribute.currentValue.value;
    } else {
      this.canSave = false;
    }
  }

  handleFormSubmit(): void {
    let value = null;

    const formValue = this.formGroup.get('field').value;

    switch (this.customAttribute.type) {
      // For select_any custom attributes
      case CustomAttributeType.SELECT_ANY:
        // Get the selected ids using the current form value and customAttribute.options array
        value = mapSelectedIdsFromFormValue(formValue, this.customAttribute.options);
        break;
      case CustomAttributeType.SELECT_ONE:
        // For select_one custom attributes we must change the formValue from a string a number
        // This is because the formValue is set via the radio input where the value(actually an id) is a string
        value = formValue === null ? null : Number(formValue);
        break;
      case CustomAttributeType.TEXT:
        // For text custom attributes we need to remove the white spaces from both sides of the string
        value = formValue.trim();
        break;
      default:
        // For text and boolean custom attributes
        value = formValue;
        break;
    }
    this.onSubmit.emit(value);
  }

  resetSelectOne(): void {
    this.formGroup.get('field').patchValue(null);
  }

  private _initForm(): void {
    if (this.customAttribute.type === CustomAttributeType.SELECT_ANY) {
      this.formGroup = this._fb.group({
        field: this._fb.array(
          this.customAttribute.options.map((option: CustomAttributeOption) =>
            this._fb.control((this.customAttribute.value as number[])?.includes(option.id) || null),
          ),
        ),
      });

      // Set the initialFormValue
      this.initialFormValue = this.customAttribute.value || null;
    } else {
      let fieldValue = this.customAttribute.value;

      if (this.customAttribute.type === CustomAttributeType.SELECT_ONE) {
        let selectedOption = null;
        if (this.customAttribute.value) {
          selectedOption = this.customAttribute.options.find(
            (option) => option.id === this.customAttribute?.value,
          );
        }
        fieldValue = selectedOption ? selectedOption.id.toString() : null;
      }

      // Set the initialFormValue
      if (this.customAttribute.type === CustomAttributeType.TEXT && !this.customAttribute.value) {
        this.initialFormValue = '';
      } else {
        this.initialFormValue = this.customAttribute.value || null;
      }

      this.formGroup = this._fb.group({
        field: [fieldValue || null],
      });
    }
  }

  private _setupFormValueTracking(): void {
    let valueChanges$ = this.formGroup.valueChanges;

    // We want to be able to save unset boolean custom attributes as false without having to change them to true first
    if (
      this.customAttribute.type === CustomAttributeType.BOOLEAN &&
      this.customAttribute.value === undefined
    ) {
      this.formGroup.get('field').patchValue(false);
      this.canSave = true;
    }

    // If autoSubmit is true and the custom attribute is of type text add a debounceTime to the valueChanges observable
    if (this.autoSubmit && this.customAttribute.type === CustomAttributeType.TEXT) {
      valueChanges$ = valueChanges$.pipe(debounceTime(500));
    }
    valueChanges$.pipe(untilDestroyed(this)).subscribe(() => {
      const formValue = this.formGroup.get('field').value;

      switch (this.customAttribute.type) {
        case CustomAttributeType.SELECT_ANY:
          // Get the selected ids using the current form value and customAttribute.options array
          const currentSelectedIds = mapSelectedIdsFromFormValue(
            formValue,
            this.customAttribute.options,
          );
          this.canSave =
            JSON.stringify(this.initialFormValue) !== JSON.stringify(currentSelectedIds);
          break;
        case CustomAttributeType.TEXT:
          this.canSave =
            formValue.trim() !== this.initialFormValue &&
            (formValue.trim().length > 0 || !this.autoSubmit);
          break;
        case CustomAttributeType.SELECT_ONE:
          this.canSave = Number(formValue) !== this.initialFormValue;
          break;
        default:
          this.canSave = formValue !== this.initialFormValue;
      }

      if (this.autoSubmit) {
        this.handleFormSubmit();
      }
    });
  }
}

const mapOptionsToRadioInputOptions = (
  options: CustomAttributeOption[],
): AccredibleRadioGroupOption[] => {
  return options.map((option) => {
    return { value: option.id.toString(), label: option.label };
  });
};

const mapSelectedIdsFromFormValue = (
  formValue: boolean[],
  options: CustomAttributeOption[],
): number[] => {
  // Loop through options and if they are set to true in the form push the id of that option to the selectedOptionIds
  return options.reduce((selectedOptionIds, option: CustomAttributeOption, index: number) => {
    if (formValue[index]) {
      selectedOptionIds.push(option.id);
    }
    return selectedOptionIds;
  }, []);
};
